import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { questionnaireService, testService } from '../services/api';
import { toast } from 'sonner';

export const LancerTestPage = () => {
  const { questionnaireId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [test, setTest] = useState(null);
  const [currentStep, setCurrentStep] = useState('info');
  const [candidateInfo, setCandidateInfo] = useState({ nom: '', prenom: '' });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentQuestion = questionnaire?.questions[currentQuestionIndex];

  // Auto-calculate rating from selections
  const calculateAutoRating = () => {
    if (!currentQuestion) return 0;
    const choices = currentQuestion.choices || [];
    const correctCount = choices.filter(c => c.is_correct).length;
    if (correctCount === 0) return 0;
    const correctSelected = selectedChoices.filter(i => choices[i]?.is_correct).length;
    const incorrectSelected = selectedChoices.filter(i => !choices[i]?.is_correct).length;
    if (incorrectSelected > 0) return Math.max(0, Math.round(((correctSelected - incorrectSelected) / correctCount) * 10 * 10) / 10);
    return Math.round((correctSelected / correctCount) * 10 * 10) / 10;
  };

  useEffect(() => {
    fetchQuestionnaire();
  }, [questionnaireId]);

  // Auto-start if coming from external page with pre-created test
  useEffect(() => {
    if (location.state?.testId && location.state?.autoStart && questionnaire) {
      setTest({ id: location.state.testId });
      setCurrentStep('test');
    }
  }, [location.state, questionnaire]);

  const fetchQuestionnaire = async () => {
    try {
      const response = await questionnaireService.getById(questionnaireId);
      setQuestionnaire(response.data);
    } catch (error) {
      toast.error('Questionnaire non trouvé');
      navigate('/questionnaires');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    if (!candidateInfo.nom || !candidateInfo.prenom) {
      toast.error('Veuillez remplir les informations du candidat');
      return;
    }

    try {
      const response = await testService.create(
        questionnaireId,
        candidateInfo.nom,
        candidateInfo.prenom
      );
      setTest(response.data);
      setCurrentStep('test');
    } catch (error) {
      toast.error('Erreur lors de la création du test');
    }
  };

  // Validate answer and move to next question (no score reveal)
  const handleValidateAndNext = async () => {
    if (selectedChoices.length === 0) {
      toast.error('Veuillez sélectionner au moins une réponse');
      return;
    }

    const autoRating = calculateAutoRating();

    try {
      await testService.answerQuestion(test.id, currentQuestionIndex, autoRating, [], selectedChoices);
      
      if (currentQuestionIndex < questionnaire.questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedChoices([]);
      } else {
        await testService.complete(test.id);
        toast.success('Test terminé avec succès');
        navigate(`/rapport/${test.id}`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  if (loading) {
    return (
      <Layout title="Lancer un test" breadcrumbs={['Accueil', 'Lancer un test']}>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title={currentStep === 'info' ? 'Informations du candidat' : 'Test en cours'} 
      breadcrumbs={['Accueil', 'Lancer un test']}
    >
      {currentStep === 'info' && (
        <div className="max-w-2xl" data-testid="candidate-info-form">
          <div className="eva-card">
            <h3 className="text-lg font-semibold text-white mb-6">
              Avant de commencer le test
            </h3>

            <div className="mb-4">
              <label className="eva-label">Nom du candidat *</label>
              <input
                type="text"
                value={candidateInfo.nom}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, nom: e.target.value.toUpperCase() })}
                className="eva-input"
                style={{ textTransform: 'uppercase' }}
                data-testid="nom-candidat-input"
              />
            </div>

            <div className="mb-6">
              <label className="eva-label">Prénom du candidat *</label>
              <input
                type="text"
                value={candidateInfo.prenom}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, prenom: e.target.value })}
                className="eva-input"
                data-testid="prenom-candidat-input"
              />
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                Questionnaire
              </p>
              <p className="text-sm font-medium text-white">{questionnaire?.fonction}</p>
              <p className="text-xs text-slate-400 mt-1">
                {questionnaire?.niveau} - {questionnaire?.questions?.length} questions
              </p>
            </div>

            <button
              onClick={handleStartTest}
              className="eva-btn-primary"
              data-testid="start-test-button"
            >
              Démarrer le test
            </button>
          </div>
        </div>
      )}

      {currentStep === 'test' && currentQuestion && (
        <div className="max-w-3xl" data-testid="test-question">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-400">
                Question {currentQuestionIndex + 1} sur {questionnaire.questions.length}
              </span>
              <span className="text-sm font-medium text-white">
                {Math.round(((currentQuestionIndex + 1) / questionnaire.questions.length) * 100)}%
              </span>
            </div>
            <div className="h-2 bg-[#E9ECEF] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-600 to-indigo-500 transition-all duration-300"
                style={{ width: `${((currentQuestionIndex + 1) / questionnaire.questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <div className="eva-card mb-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                  Question {currentQuestionIndex + 1}
                </p>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#3B82F6]/10 text-[#3B82F6]">
                  QCM
                </span>
              </div>
              <h3 className="text-xl font-medium text-white leading-relaxed">
                {currentQuestion.text}
              </h3>
            </div>

            {/* QCM Choices */}
            {currentQuestion.choices && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
                  Cochez les réponses données par le candidat
                </p>
                <ul className="space-y-2">
                  {currentQuestion.choices.map((choice, i) => (
                    <li key={i} className="text-sm flex items-start gap-3 cursor-pointer hover:bg-[#0B132B] p-2 rounded transition-colors"
                        onClick={() => {
                          setSelectedChoices(prev =>
                            prev.includes(i) ? prev.filter(idx => idx !== i) : [...prev, i]
                          );
                        }}
                        data-testid={`choice-checkbox-${i}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedChoices.includes(i)}
                        onChange={() => {}}
                        className="mt-0.5 w-4 h-4 cursor-pointer"
                      />
                      <span className={selectedChoices.includes(i) ? 'font-medium text-white' : 'text-slate-300'}>
                        {choice.text}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 p-2 bg-[#0B132B] rounded">
                  <p className="text-xs text-slate-400">
                    {selectedChoices.length} choix sélectionné(s)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="eva-card" data-testid="validation-section">
            <button
              onClick={handleValidateAndNext}
              className="eva-btn-primary w-full"
              data-testid="validate-answer-button"
            >
              {currentQuestionIndex < questionnaire.questions.length - 1
                ? 'Valider et passer à la question suivante'
                : 'Valider et terminer le test'}
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};
